/**
 * RequireGlobalAdmin — route-level guard for admin-only surfaces.
 *
 * Phase D (TASK_004 — 2026-06-07): direct-URL navigation to admin pages
 * (e.g. `/admin/ready-made-packs/authoring`, `/app/ops`, `/settings/users`)
 * used to load the page, fire admin API calls, then render a wall of 403s
 * because the topnav/sidebar hiding work in Phase C only covered visibility.
 * This guard wraps the routes so non-admins are redirected to `/projects`
 * and shown a Snackbar explaining why.
 *
 * Trust contract: `isGlobalAdmin` is now the authoritative client-side flag
 * (Phase A + C). The backend signin profile + `/api/global-admins/me` are
 * the single source of truth; this guard simply honors that flag.
 *
 * Loading semantics: while `loading === true` we render a spinner. We MUST
 * NOT navigate during loading — otherwise the dev-login / demo restore
 * paths (which have `currentUser=null` for a few render cycles) would
 * ping-pong to `/projects` even for valid admin users.
 */
import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Box, CircularProgress } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { useAuth } from '../../contexts/AuthContext'
import { useToast } from '../../contexts/ToastContext'

interface RequireGlobalAdminProps {
  children: React.ReactNode
  /**
   * Optional redirect target. Defaults to `/projects` (the universally-safe
   * landing page that every logged-in user can reach).
   */
  redirectTo?: string
}

const RequireGlobalAdmin: React.FC<RequireGlobalAdminProps> = ({
  children,
  redirectTo = '/projects',
}) => {
  const { isGlobalAdmin, loading } = useAuth()
  const { showError } = useToast()
  const { t } = useTranslation()
  const location = useLocation()
  const toastFiredRef = React.useRef(false)

  // Fire the Snackbar exactly once per denied navigation. We key the ref by
  // pathname so a user bouncing between admin URLs in the same session sees
  // a fresh toast each time (rather than a silent redirect after the first).
  React.useEffect(() => {
    if (loading) return
    if (isGlobalAdmin) {
      toastFiredRef.current = false
      return
    }
    if (toastFiredRef.current) return
    toastFiredRef.current = true
    showError(
      t(
        'auth.guard.globalAdminOnly',
        '이 페이지는 글로벌 관리자만 이용할 수 있습니다.'
      )
    )
  }, [isGlobalAdmin, loading, location.pathname, showError, t])

  if (loading) {
    return (
      <Box sx={{ p: 6, textAlign: 'center' }} data-testid="require-global-admin-loading">
        <CircularProgress />
      </Box>
    )
  }

  if (!isGlobalAdmin) {
    return <Navigate to={redirectTo} replace />
  }

  return <>{children}</>
}

export default RequireGlobalAdmin
